import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Popover,
  TextField,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { useTranslation } from "react-i18next";
import { ReducerStates, RoleTypes } from "../../helpers/constants";
import { ICommunities } from "../../services/header.services";
import { ISCIMGroupMappingData } from "../../services/settings.services";
import { useAppDispatch, useAppSelector } from "../app/appHooks";
import { roleLabels } from "./common/RolePicker";
import {
  createOrganizationSCIMGroupMapping,
  loadOrganizationSCIMGroups,
  updateOrganizationSCIMGroupMapping,
} from "./settingsThunks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(5.5, 6),
      width: "860px",
    },
    sectionTitle: {
      marginTop: 0,
      marginBottom: theme.spacing(6),
      textAlign: "center",
    },
    table: {
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: "760px",
      width: "100%",
    },
    headerRow: {
      display: "grid",
      gridTemplateColumns: "230px minmax(0, 1fr) 150px 72px",
      columnGap: theme.spacing(1.5),
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
    },
    mappingRow: {
      alignItems: "center",
      border: "1px solid rgba(0, 0, 0, 0.18)",
      borderRadius: "4px",
      boxSizing: "border-box",
      display: "grid",
      gridTemplateColumns: "230px minmax(0, 1fr) 150px 72px",
      columnGap: theme.spacing(1.5),
      minHeight: "40px",
      marginTop: theme.spacing(1),
      padding: theme.spacing(0, 1.5),
    },
    groupName: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    selectorButton: {
      alignItems: "center",
      borderBottom: "1px solid rgba(0, 0, 0, 0.35)",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      minHeight: "24px",
      minWidth: 0,
      textAlign: "left",
    },
    selectorText: {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    placeholder: {
      color: theme.palette.error.main,
    },
    applyButton: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      height: "28px",
      justifySelf: "start",
      minWidth: "64px",
      padding: theme.spacing(0.25, 0.5),
      textTransform: "none",
    },
    popover: {
      padding: theme.spacing(1.5),
      width: "320px",
    },
    popoverActions: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    popoverList: {
      maxHeight: "280px",
      overflowY: "auto",
      paddingTop: theme.spacing(1),
    },
    popoverItem: {
      paddingBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5),
    },
    checkbox: {
      color: theme.palette.primary.main,
      padding: theme.spacing(0.25, 1, 0.25, 0),
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
    emptyText: {
      marginTop: theme.spacing(3),
      textAlign: "center",
    },
    errorText: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(2),
      textAlign: "center",
    },
  })
);

interface IGroupMappingDraft {
  communityIDs: string[];
  roleName: string | null;
}

type SelectorTarget =
  | { type: "communities"; groupID: string }
  | { type: "role"; groupID: string }
  | null;

const getCommunityLabel = (communities: ICommunities[], communityID: string) =>
  communities.find((community) => community.id === communityID)?.name ??
  communityID;

const sameStringSet = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;

  const rightSet = new Set(right);
  return left.every((value) => rightSet.has(value));
};

const draftFromGroup = (group: {
  community_ids: string[];
  role_name: string | null;
}) => ({
  communityIDs: group.community_ids ?? [],
  roleName: group.role_name,
});

export function GroupMapping() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedOrganizationID = useAppSelector(
    (state) => state.headerState.selectedOrganization
  );
  const communities = useAppSelector(
    (state) => state.headerState.communities ?? []
  );
  const userRoles = useAppSelector((state) => state.headerState.userRoles);
  const scimGroups = useAppSelector((state) => state.settingsState.scimGroups);
  const scimGroupsState = useAppSelector(
    (state) => state.settingsState.scimGroupsState
  );
  const [drafts, setDrafts] = useState<Record<string, IGroupMappingDraft>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectorTarget, setSelectorTarget] = useState<SelectorTarget>(null);
  const [communitySearch, setCommunitySearch] = useState("");
  const [pendingGroupID, setPendingGroupID] = useState<string | null>(null);
  const [applyError, setApplyError] = useState("");

  const organizationCommunities = useMemo(
    () =>
      communities.filter(
        (community) => community.organization_id === selectedOrganizationID
      ),
    [communities, selectedOrganizationID]
  );

  const mappingRoles = useMemo(
    () =>
      userRoles?.filter((role) =>
        [RoleTypes.admin, RoleTypes.caregiver, RoleTypes.installer].includes(
          role.name as RoleTypes
        )
      ) ?? [],
    [userRoles]
  );

  useEffect(() => {
    if (selectedOrganizationID !== undefined) {
      dispatch(loadOrganizationSCIMGroups(selectedOrganizationID));
    }
  }, [dispatch, selectedOrganizationID]);

  const handleOpenSelector = (
    event: React.MouseEvent<HTMLElement>,
    target: SelectorTarget
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectorTarget(target);
    setCommunitySearch("");
  };

  const handleCloseSelector = () => {
    setAnchorEl(null);
    setSelectorTarget(null);
    setCommunitySearch("");
  };

  const updateDraft = (
    groupID: string,
    updater: (draft: IGroupMappingDraft) => IGroupMappingDraft
  ) => {
    setDrafts((current) => ({
      ...current,
      [groupID]: updater(
        current[groupID] ?? { communityIDs: [], roleName: null }
      ),
    }));
  };

  const handleToggleCommunity = (groupID: string, communityID: string) => {
    updateDraft(groupID, (draft) => {
      const selected = draft.communityIDs.includes(communityID);
      return {
        ...draft,
        communityIDs: selected
          ? draft.communityIDs.filter((id) => id !== communityID)
          : [...draft.communityIDs, communityID],
      };
    });
  };

  const handleToggleAllCommunities = (groupID: string) => {
    const allCommunityIDs = organizationCommunities.map(
      (community) => community.id
    );
    const draft = drafts[groupID] ?? { communityIDs: [], roleName: null };
    const allSelected = sameStringSet(draft.communityIDs, allCommunityIDs);
    updateDraft(groupID, (current) => ({
      ...current,
      communityIDs: allSelected ? [] : allCommunityIDs,
    }));
  };

  const handleRoleChange = (groupID: string, roleName: string) => {
    updateDraft(groupID, (draft) => ({
      ...draft,
      roleName,
    }));
    handleCloseSelector();
  };

  const getCommunityDisplay = (draft: IGroupMappingDraft | undefined) => {
    if (draft === undefined || draft.communityIDs.length === 0) {
      return t("select_community");
    }

    if (draft.communityIDs.length === organizationCommunities.length) {
      return t("all_communities");
    }

    return draft.communityIDs
      .map((communityID) =>
        getCommunityLabel(organizationCommunities, communityID)
      )
      .join(", ");
  };

  const isDirty = (groupID: string) => {
    const group = scimGroups.find((item) => item.scim_group_id === groupID);
    const draft = drafts[groupID];
    if (group === undefined || draft === undefined) return false;

    return (
      !sameStringSet(group.community_ids ?? [], draft.communityIDs) ||
      group.role_name !== draft.roleName
    );
  };

  useEffect(() => {
    if (scimGroupsState !== ReducerStates.SUCCEEDED) return;

    setDrafts((currentDrafts) => {
      const nextDrafts: Record<string, IGroupMappingDraft> = {};
      scimGroups.forEach((group) => {
        const currentDraft = currentDrafts[group.scim_group_id];
        const serverDraft = draftFromGroup(group);
        const draftIsDirty =
          currentDraft !== undefined &&
          (!sameStringSet(
            group.community_ids ?? [],
            currentDraft.communityIDs
          ) ||
            group.role_name !== currentDraft.roleName);

        nextDrafts[group.scim_group_id] = draftIsDirty
          ? currentDraft
          : serverDraft;
      });
      return nextDrafts;
    });
  }, [scimGroups, scimGroupsState]);

  const canApply = (groupID: string) => {
    const group = scimGroups.find((item) => item.scim_group_id === groupID);
    const draft = drafts[groupID];
    if (group === undefined || draft === undefined) return false;

    return (
      draft.communityIDs.length > 0 &&
      draft.roleName !== null &&
      (isDirty(groupID) || !group.is_mapped)
    );
  };

  const handleApply = async (groupID: string) => {
    if (selectedOrganizationID === undefined || !canApply(groupID)) return;

    const group = scimGroups.find((item) => item.scim_group_id === groupID);
    const draft = drafts[groupID];
    if (group === undefined || draft === undefined || draft.roleName === null) {
      return;
    }

    const mappingData: ISCIMGroupMappingData = {
      scim_group_id: group.scim_group_id,
      scim_group_name: group.scim_group_name,
      community_ids: draft.communityIDs,
      role_name: draft.roleName,
      all_units: true,
      notification_group_id: null,
    };

    setPendingGroupID(groupID);
    setApplyError("");
    const action =
      group.is_mapped && group.mapping_ids.length > 0
        ? await dispatch(
            updateOrganizationSCIMGroupMapping({
              mappingID: group.mapping_ids[0],
              mappingData,
            })
          )
        : await dispatch(
            createOrganizationSCIMGroupMapping({
              organizationID: selectedOrganizationID,
              mappingData,
            })
          );

    if (
      createOrganizationSCIMGroupMapping.fulfilled.match(action) ||
      updateOrganizationSCIMGroupMapping.fulfilled.match(action)
    ) {
      setDrafts((currentDrafts) => ({
        ...currentDrafts,
        [groupID]: {
          communityIDs: draft.communityIDs,
          roleName: draft.roleName,
        },
      }));
      dispatch(loadOrganizationSCIMGroups(selectedOrganizationID));
    } else {
      setApplyError(t("something_happened"));
    }
    setPendingGroupID(null);
  };

  const filteredCommunities = organizationCommunities.filter((community) =>
    community.name.toLowerCase().includes(communitySearch.toLowerCase())
  );

  const activeGroupID = selectorTarget?.groupID;
  const activeDraft =
    activeGroupID !== undefined ? drafts[activeGroupID] : undefined;

  return (
    <Paper className={classes.container} elevation={0} square>
      <h2 className={classes.sectionTitle}>{t("group_mapping")}</h2>
      {scimGroupsState === ReducerStates.PENDING ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : scimGroupsState === ReducerStates.FAILED ? (
        <Typography className={classes.errorText}>
          {t("something_happened")}
        </Typography>
      ) : scimGroups.length === 0 ? (
        <Typography className={classes.emptyText}>
          {t("no_entra_groups_synced")}
        </Typography>
      ) : (
        <div className={classes.table}>
          <div className={classes.headerRow}>
            <div>{t("group")}</div>
            <div>{t("community")}</div>
            <div>{t("role")}</div>
            <div />
          </div>
          {scimGroups.map((group) => {
            const draft = drafts[group.scim_group_id];
            const roleLabel =
              draft?.roleName !== null && draft?.roleName !== undefined
                ? t(roleLabels[draft.roleName] ?? draft.roleName)
                : t("select_role");

            return (
              <div className={classes.mappingRow} key={group.scim_group_id}>
                <div
                  className={classes.groupName}
                  title={group.scim_group_name}
                >
                  {group.scim_group_name}
                </div>
                <div
                  className={classes.selectorButton}
                  onClick={(event) =>
                    handleOpenSelector(event, {
                      type: "communities",
                      groupID: group.scim_group_id,
                    })
                  }
                  role="button"
                  tabIndex={0}
                >
                  <span
                    className={`${classes.selectorText} ${
                      draft === undefined || draft.communityIDs.length === 0
                        ? classes.placeholder
                        : ""
                    }`}
                  >
                    {getCommunityDisplay(draft)}
                  </span>
                  <ArrowDropDownIcon />
                </div>
                <div
                  className={classes.selectorButton}
                  onClick={(event) =>
                    handleOpenSelector(event, {
                      type: "role",
                      groupID: group.scim_group_id,
                    })
                  }
                  role="button"
                  tabIndex={0}
                >
                  <span
                    className={`${classes.selectorText} ${
                      draft?.roleName === null || draft?.roleName === undefined
                        ? classes.placeholder
                        : ""
                    }`}
                  >
                    {roleLabel}
                  </span>
                  <ArrowDropDownIcon />
                </div>
                <Button
                  className={classes.applyButton}
                  disabled={
                    !canApply(group.scim_group_id) ||
                    pendingGroupID === group.scim_group_id
                  }
                  onClick={() => handleApply(group.scim_group_id)}
                >
                  {pendingGroupID === group.scim_group_id ? (
                    <CircularProgress size={14} />
                  ) : (
                    t("apply")
                  )}
                </Button>
              </div>
            );
          })}
          {applyError && (
            <Typography className={classes.errorText}>{applyError}</Typography>
          )}
        </div>
      )}
      <Popover
        anchorEl={anchorEl}
        open={anchorEl !== null && selectorTarget !== null}
        onClose={handleCloseSelector}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {selectorTarget?.type === "communities" && activeGroupID && (
          <div className={classes.popover}>
            <TextField
              fullWidth
              onChange={(event) => setCommunitySearch(event.target.value)}
              placeholder={t("search")}
              value={communitySearch}
            />
            <div className={classes.popoverActions}>
              <Button
                color="primary"
                onClick={() => handleToggleAllCommunities(activeGroupID)}
              >
                {t("select_all_communities")}
              </Button>
            </div>
            <List className={classes.popoverList}>
              {filteredCommunities.map((community) => (
                <ListItem
                  button
                  className={classes.popoverItem}
                  key={community.id}
                  onClick={() =>
                    handleToggleCommunity(activeGroupID, community.id)
                  }
                >
                  <Checkbox
                    checked={
                      activeDraft?.communityIDs.includes(community.id) ?? false
                    }
                    className={classes.checkbox}
                    disableRipple
                  />
                  {community.name}
                </ListItem>
              ))}
            </List>
          </div>
        )}
        {selectorTarget?.type === "role" && activeGroupID && (
          <List>
            {mappingRoles.map((role) => (
              <ListItem
                button
                key={role.name}
                onClick={() => handleRoleChange(activeGroupID, role.name)}
              >
                {t(roleLabels[role.name] ?? role.name)}
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </Paper>
  );
}
